import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { X, Calendar, Film, Languages as LangIcon, Mail, Phone, Linkedin, Send, MessageCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Enquiry Modal (in-page contact form + direct phone/LinkedIn) ──
const PHONE_DISPLAY = "+91 93420 55964";
const PHONE_DIGITS = "919342055964";
const WHATSAPP_URL = `https://wa.me/${PHONE_DIGITS}`;
const TEL_URL = "tel:+919342055964";
const LINKEDIN_URL = "https://www.linkedin.com/in/madhiee/";

const EnquiryModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (open) {
      setSubmitted(false);
      setForm({ name: "", email: "", company: "", message: "" });
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Name, email and message are required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Message sent — we'll be in touch shortly.");
    } catch {
      toast.error("Could not send. Try WhatsApp or LinkedIn instead.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid="enquiry-modal"
        className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto"
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.96, opacity: 0, y: 20 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => e.stopPropagation()}
          className="relative bg-[#0B0B0D] border border-[#1f1f23] rounded-2xl overflow-hidden max-w-2xl w-full my-8 shadow-2xl shadow-[#17B352]/10"
        >
          {/* Header */}
          <div className="relative p-6 md:p-8 border-b border-[#1f1f23]">
            <button
              data-testid="enquiry-modal-close"
              onClick={onClose}
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-[#111114] hover:bg-[#1f1f23] flex items-center justify-center text-[#A1A1AA] hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            <p className="mono-font text-[10px] tracking-[0.3em] text-[#17B352] uppercase mb-2">Let's talk</p>
            <h2 className="text-3xl md:text-4xl italic leading-tight" style={{ fontFamily: "'Instrument Serif', serif" }}>
              Start a conversation
            </h2>
            <p className="text-[#A1A1AA] text-sm mt-2">
              Tell us about your project, or reach out directly — whichever feels right.
            </p>
          </div>

          {/* Direct contact rail */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-6 md:px-8 md:pt-6 md:pb-2">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="enquiry-whatsapp"
              className="group flex items-center gap-3 p-4 rounded-xl border border-[#1f1f23] hover:border-[#32D74B]/50 hover:bg-[rgba(50,215,75,0.04)] transition-all"
            >
              <div className="w-10 h-10 rounded-full bg-[rgba(50,215,75,0.1)] border border-[#32D74B]/30 flex items-center justify-center shrink-0">
                <MessageCircle className="w-4 h-4 text-[#32D74B]" />
              </div>
              <div className="min-w-0">
                <p className="mono-font text-[10px] tracking-[0.2em] text-[#52525B] uppercase">WhatsApp · Phone</p>
                <p className="text-sm text-white font-medium truncate">{PHONE_DISPLAY}</p>
              </div>
            </a>
            <a
              href={LINKEDIN_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="enquiry-linkedin"
              className="group flex items-center gap-3 p-4 rounded-xl border border-[#1f1f23] hover:border-[#0A66C2]/60 hover:bg-[rgba(10,102,194,0.05)] transition-all"
            >
              <div className="w-10 h-10 rounded-full bg-[rgba(10,102,194,0.1)] border border-[#0A66C2]/40 flex items-center justify-center shrink-0">
                <Linkedin className="w-4 h-4 text-[#4A9CFF]" />
              </div>
              <div className="min-w-0">
                <p className="mono-font text-[10px] tracking-[0.2em] text-[#52525B] uppercase">LinkedIn</p>
                <p className="text-sm text-white font-medium truncate">linkedin.com/in/madhiee</p>
              </div>
            </a>
          </div>

          {/* Divider */}
          <div className="px-6 md:px-8 my-4 flex items-center gap-3">
            <div className="flex-1 h-px bg-[#1f1f23]" />
            <span className="mono-font text-[10px] tracking-[0.3em] text-[#52525B] uppercase">or send a message</span>
            <div className="flex-1 h-px bg-[#1f1f23]" />
          </div>

          {/* Form */}
          {submitted ? (
            <div className="px-6 md:px-8 pb-8 text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[rgba(50,215,75,0.1)] border border-[#32D74B]/40 mb-4">
                <Send className="w-5 h-5 text-[#32D74B]" />
              </div>
              <h3 className="text-2xl italic mb-2" style={{ fontFamily: "'Instrument Serif', serif" }}>Got it. Talk soon.</h3>
              <p className="text-[#A1A1AA] text-sm mb-6">Your message has reached our inbox. Expect a reply within one business day.</p>
              <button
                onClick={onClose}
                data-testid="enquiry-success-close"
                className="mono-font text-xs uppercase tracking-[0.2em] px-5 py-2.5 rounded-full border border-[#1f1f23] text-[#A1A1AA] hover:border-[#17B352] hover:text-[#17B352] transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="p-6 md:px-8 md:pb-8 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  data-testid="enquiry-name"
                  type="text"
                  placeholder="Your name *"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 bg-[#111114] border border-[#1f1f23] rounded-lg text-sm text-white placeholder-[#52525B] focus:border-[#17B352]/60 focus:outline-none transition-colors"
                  required
                />
                <input
                  data-testid="enquiry-email"
                  type="email"
                  placeholder="Email *"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 bg-[#111114] border border-[#1f1f23] rounded-lg text-sm text-white placeholder-[#52525B] focus:border-[#17B352]/60 focus:outline-none transition-colors"
                  required
                />
              </div>
              <input
                data-testid="enquiry-company"
                type="text"
                placeholder="Company (optional)"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className="w-full px-4 py-3 bg-[#111114] border border-[#1f1f23] rounded-lg text-sm text-white placeholder-[#52525B] focus:border-[#17B352]/60 focus:outline-none transition-colors"
              />
              <textarea
                data-testid="enquiry-message"
                placeholder="Tell us about your project, languages, timeline... *"
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full px-4 py-3 bg-[#111114] border border-[#1f1f23] rounded-lg text-sm text-white placeholder-[#52525B] focus:border-[#17B352]/60 focus:outline-none transition-colors resize-none"
                required
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="enquiry-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#17B352] hover:bg-[#22D060] disabled:opacity-60 disabled:cursor-not-allowed text-black mono-font text-xs uppercase tracking-[0.2em] font-bold px-6 py-3.5 rounded-full transition-colors"
              >
                <Send className="w-4 h-4" /> {submitting ? "Sending..." : "Send message"}
              </button>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ── Compact Show Card (used inside marquee rows) ──
const RowCard = ({ show, onClick }) => {
  const [imageError, setImageError] = useState(false);
  return (
    <button
      data-testid={`show-card-${show.id}`}
      onClick={() => onClick(show)}
      className="group relative shrink-0 w-[140px] md:w-[180px] aspect-[2/3] rounded-lg overflow-hidden bg-[#111114] border border-[#1f1f23] hover:border-[#17B352]/50 transition-all text-left"
    >
      {show.poster_url && !imageError ? (
        <img
          src={show.poster_url}
          alt={show.title}
          loading="lazy"
          onError={() => setImageError(true)}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#1a1a1c] to-[#0B0B0D] p-3">
          <span className="text-center italic text-[#52525B] text-base leading-tight" style={{ fontFamily: "'Instrument Serif', serif" }}>{show.title}</span>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/20 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
      <div className="absolute bottom-0 left-0 right-0 p-2.5">
        <p className="text-xs md:text-sm font-medium text-white line-clamp-2 leading-tight">{show.title}</p>
        <div className="flex items-center gap-1.5 mt-1 mono-font text-[9px] text-[#A1A1AA] tracking-wider uppercase">
          {show.year && <span>{show.year}</span>}
          {show.year && <span className="text-[#3f3f46]">·</span>}
          <span>{show.type}</span>
        </div>
      </div>
    </button>
  );
};

// ── Auto-scrolling marquee row ──
const MarqueeRow = ({ title, subtitle, items, onClickShow, durationSec = 80, reverse = false }) => {
  if (!items || items.length === 0) return null;
  const minRepeats = items.length < 8 ? Math.ceil(16 / items.length) : 2;
  const looped = Array.from({ length: minRepeats }).flatMap(() => items);
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

  return (
    <section className="mb-10" data-testid={`row-${slug}`}>
      <div className="max-w-7xl mx-auto px-6 mb-3">
        <h3 className="mono-font text-[11px] tracking-[0.3em] text-[#17B352] uppercase">{title}</h3>
        {subtitle && (
          <p className="text-[#A1A1AA] text-sm mt-1 max-w-2xl leading-relaxed">{subtitle}</p>
        )}
      </div>
      <div className="marquee-wrap">
        <div
          className={`marquee-track ${reverse ? "reverse" : ""}`}
          style={{ animationDuration: `${durationSec}s` }}
        >
          {looped.map((show, i) => (
            <RowCard key={`${show.id}-${i}`} show={show} onClick={onClickShow} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ── Text-only chip used for items without posters ──
const TextChip = ({ show, onClick }) => (
  <button
    data-testid={`text-chip-${show.id}`}
    onClick={() => onClick(show)}
    className="shrink-0 px-4 py-2 rounded-full bg-[#111114] border border-[#1f1f23] hover:border-[#17B352]/50 hover:text-[#17B352] mono-font text-[11px] tracking-[0.1em] text-[#A1A1AA] uppercase transition-colors whitespace-nowrap"
  >
    {show.title}
    {show.year ? <span className="text-[#3f3f46] ml-2">{show.year}</span> : null}
  </button>
);

// ── Text-only chip marquee row (for poster-less items) ──
const ChipRow = ({ title, subtitle, items, onClickShow }) => {
  if (!items || items.length === 0) return null;
  const minRepeats = items.length < 30 ? Math.ceil(60 / items.length) : 2;
  const looped = Array.from({ length: minRepeats }).flatMap(() => items);
  return (
    <section className="mb-10" data-testid="row-also-in-catalog">
      <div className="max-w-7xl mx-auto px-6 mb-3">
        <h3 className="mono-font text-[11px] tracking-[0.3em] text-[#17B352] uppercase">{title}</h3>
        {subtitle && (
          <p className="text-[#A1A1AA] text-sm mt-1 max-w-2xl leading-relaxed">{subtitle}</p>
        )}
      </div>
      <div className="marquee-wrap">
        <div className="marquee-track" style={{ animationDuration: `${Math.min(120, 30 + items.length * 0.4)}s` }}>
          {looped.map((show, i) => (
            <TextChip key={`${show.id}-${i}`} show={show} onClick={onClickShow} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ── Client Logos Strip (real logos via logo.dev → duckduckgo → wordmark fallback) ──
const LOGO_DEV_TOKEN = "pk_X-1ZO13GSgeOoUrIuJ6GMQ";

const CLIENT_LOGOS = [
  { name: "Iyuno",          domain: "iyuno.com",         wordmarkStyle: { fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: "26px", letterSpacing: "-0.04em", textTransform: "lowercase" } },
  { name: "Deluxe",         domain: "bydeluxe.com",      wordmarkStyle: { fontFamily: "'Bebas Neue', sans-serif", fontWeight: 400, fontSize: "30px", letterSpacing: "0.18em", textTransform: "uppercase" } },
  { name: "Zoo Digital",    domain: "zoodigital.com",    wordmarkStyle: { fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontSize: "32px", letterSpacing: "-0.02em" } },
  { name: "Facebook",       domain: "facebook.com",      wordmarkStyle: { fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: "24px", letterSpacing: "-0.03em" } },
  { name: "Disney+ Hotstar",domain: "hotstar.com",       wordmarkStyle: { fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: "20px" }, plus: true },
  { name: "AGARO",          domain: "agarolifestyle.com",wordmarkStyle: { fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: "26px", letterSpacing: "0.22em" } },
  { name: "Disano",         domain: "disanofoods.com",   wordmarkStyle: { fontFamily: "'Instrument Serif', serif", fontSize: "30px", letterSpacing: "-0.01em" } },
];

const LogoMark = ({ logo }) => {
  const sources = [
    `https://img.logo.dev/${logo.domain}?token=${LOGO_DEV_TOKEN}&size=200&retina=true`,
    `https://icons.duckduckgo.com/ip3/${logo.domain}.ico`,
  ];
  const [idx, setIdx] = useState(0);
  const [failed, setFailed] = useState(false);
  const testid = `client-logo-${logo.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`;

  if (failed) {
    return (
      <div data-testid={testid} className="client-logo shrink-0 px-10 py-4 flex items-center justify-center text-[#A1A1AA] select-none" style={logo.wordmarkStyle}>
        {logo.plus ? (
          <span>Disney<sup className="text-[#17B352]" style={{ fontSize: "0.55em", verticalAlign: "super" }}>+</sup>{" "}Hotstar</span>
        ) : (
          logo.name
        )}
      </div>
    );
  }

  return (
    <div data-testid={testid} className="client-logo shrink-0 px-8 py-3 flex items-center justify-center min-w-[160px]">
      <img
        src={sources[idx]}
        alt={logo.name}
        onError={() => {
          if (idx + 1 < sources.length) setIdx(idx + 1);
          else setFailed(true);
        }}
        className="h-10 md:h-12 max-w-[160px] object-contain"
        loading="lazy"
      />
    </div>
  );
};

const ClientLogosStrip = () => {
  const doubled = [...CLIENT_LOGOS, ...CLIENT_LOGOS];
  return (
    <section data-testid="client-logos-strip" className="mb-12 mt-2">
      <div className="max-w-7xl mx-auto px-6 mb-4 flex items-center gap-3">
        <span className="mono-font text-[10px] tracking-[0.3em] text-[#52525B] uppercase">Trusted by</span>
        <div className="flex-1 h-px bg-[#1f1f23]" />
      </div>
      <div className="marquee-wrap">
        <div className="marquee-track" style={{ animationDuration: "55s" }}>
          {doubled.map((logo, i) => (
            <LogoMark key={`${logo.name}-${i}`} logo={logo} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ── Task definitions (shown as service-row subtitles) ──
const TASK_DEFINITIONS = {
  "Timecoding": "Frame-accurate spotting & in/out timing for every subtitle line",
  "Timecoding (Empty)": "Blank timecoded templates ready for translator input",
  "QC (AS)": "As-Sent final quality control — last delivery-readiness pass",
  "QC (MT)": "Machine-translation quality check, pre-human review",
  "Subtitle QC": "Linguistic and technical verification of subtitle files",
  "Subtitle Reformat": "Adapting subtitles across platforms, codecs and specs",
  "Linguistic QC": "Native-speaker review of translation accuracy and nuance",
  "Translation": "Source-to-target script translation by certified linguists",
  "First Translation": "Initial linguistic pass on raw, untranslated material",
  "Client Guideline Compliance": "Adherence to each client's style guide and house specs",
};

const describeTask = (svc) =>
  TASK_DEFINITIONS[svc] || "Specialised localisation craft delivered at broadcast quality";


// ── Show Detail Modal ──
const ShowModal = ({ show, onClose }) => {
  const [imageError, setImageError] = useState(false);
  if (!show) return null;
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => e.stopPropagation()}
          className="relative bg-[#0B0B0D] border border-[#1f1f23] rounded-xl overflow-hidden max-w-3xl w-full my-8"
        >
          <div className="relative h-64 md:h-80 overflow-hidden">
            {show.poster_url && !imageError ? (
              <>
                <img src={show.poster_url} alt={show.title} onError={() => setImageError(true)}
                  className="absolute inset-0 w-full h-full object-cover opacity-50" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0D] via-[#0B0B0D]/70 to-transparent" />
              </>
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1c] to-[#0B0B0D]" />
            )}
            <button data-testid="show-modal-close" onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/60 hover:bg-black/90 flex items-center justify-center text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <h2 className="text-3xl md:text-4xl text-white italic mb-2" style={{ fontFamily: "'Instrument Serif', serif" }}>{show.title}</h2>
              <div className="flex flex-wrap gap-3 mono-font text-xs text-[#A1A1AA] uppercase tracking-wider">
                {show.year && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{show.year}</span>}
                <span className="flex items-center gap-1"><Film className="w-3 h-3" />{show.type}</span>
                {show.client && <span className="text-[#17B352]">{show.client}</span>}
              </div>
            </div>
          </div>
          <div className="p-6 md:p-8 space-y-6">
            {show.description && <p className="text-[#A1A1AA] leading-relaxed">{show.description}</p>}
            {show.languages?.length > 0 && (
              <div>
                <h4 className="mono-font text-[10px] text-[#52525B] uppercase tracking-wider mb-3 flex items-center gap-2">
                  <LangIcon className="w-3 h-3" /> Languages Delivered
                </h4>
                <div className="flex flex-wrap gap-2">
                  {show.languages.map((lang, i) => (
                    <span key={i} className="mono-font text-xs px-3 py-1.5 rounded-full border border-[#1f1f23] text-[#A1A1AA] bg-[rgba(255,255,255,0.02)]">
                      {lang}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {show.services?.length > 0 && (
              <div>
                <h4 className="mono-font text-[10px] text-[#17B352] uppercase tracking-wider mb-3">Services We Delivered</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {show.services.map((svc, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[rgba(23,179,82,0.05)] border border-[#17B352]/15">
                      <div className="w-1 h-1 rounded-full bg-[#17B352]" />
                      <span className="text-sm text-[#E5E5E5]">{svc}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ── Helpers ──
const shortClient = (name) => {
  if (!name) return "Unknown";
  return name
    .replace(/\s*\(via Iyuno\)\s*/i, "")
    .replace(/\s*\(20\d{2}\)\s*/g, "")
    .replace(/\s*\(Southeast Asia\) Pte\. Ltd\./i, "")
    .replace(/\s*Technical Operations\b/i, "")
    .replace(/\s*Pictures Entertainment\b/i, " Pictures")
    .replace(/\s*Pictures International\b/i, " Pictures")
    .replace(/\s*International TV\b/i, " International")
    .replace(/\s*\.com Services LLC\b/i, "")
    .replace(/\s*Studios International\b/i, " Studios")
    .replace(/\s*Productions LLC\b/i, "")
    .trim();
};

// ── Portfolio Page ──
const PortfolioPage = () => {
  const [shows, setShows] = useState([]);
  const [display, setDisplay] = useState({ show_no_poster: false, show_client_logos: true });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [enquiryOpen, setEnquiryOpen] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/portfolio`).then((r) => setShows(r.data || [])).catch(() => {}),
      axios.get(`${API}/portfolio/display-settings`).then((r) => setDisplay(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  // Latest split (with vs without poster)
  const latestWithPoster = useMemo(() => shows.filter((s) => s.is_latest && s.poster_url), [shows]);
  const fullDbWithPoster = useMemo(() => shows.filter((s) => !s.is_latest && s.poster_url), [shows]);
  const allNoPoster = useMemo(() => shows.filter((s) => !s.poster_url), [shows]);

  const { serviceRows, clientRows } = useMemo(() => {
    const svcMap = new Map();
    const cliMap = new Map();
    fullDbWithPoster.forEach((s) => {
      (s.services || []).forEach((svc) => {
        if (!svc) return;
        if (!svcMap.has(svc)) svcMap.set(svc, []);
        svcMap.get(svc).push(s);
      });
      const c = shortClient(s.client);
      if (c && c !== "Unknown") {
        if (!cliMap.has(c)) cliMap.set(c, []);
        cliMap.get(c).push(s);
      }
    });
    const sortBucket = (arr) => [...arr].slice(0, 40);
    const topServices = [...svcMap.entries()]
      .filter(([, v]) => v.length >= 6)
      .sort((a, b) => b[1].length - a[1].length)
      .slice(0, 4)
      .map(([k, v]) => ({ key: k, items: sortBucket(v), count: v.length }));
    const topClients = [...cliMap.entries()]
      .sort((a, b) => b[1].length - a[1].length)
      .slice(0, 4)
      .map(([k, v]) => ({ key: k, items: sortBucket(v), count: v.length }));
    return { serviceRows: topServices, clientRows: topClients };
  }, [fullDbWithPoster]);

  const openEnquiry = (e) => {
    e?.preventDefault?.();
    setEnquiryOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-[#E5E5E5]">
      <nav className="fixed top-0 left-0 right-0 z-40 nav-scrolled">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center"><img src="/graphikpro-logo.png" alt="Graphikpro" className="h-8 w-auto" /></Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="mono-font text-xs tracking-[0.2em] text-[#A1A1AA] hover:text-[#17B352] transition-colors uppercase">Home</Link>
            <Link to="/portfolio" className="mono-font text-xs tracking-[0.2em] text-[#17B352] uppercase">Portfolio</Link>
            <Link to="/about" className="mono-font text-xs tracking-[0.2em] text-[#A1A1AA] hover:text-[#17B352] transition-colors uppercase">About</Link>
          </div>
        </div>
      </nav>

      {/* Sticky right-side Enquire CTA */}
      <button
        type="button"
        data-testid="sticky-enquire-cta"
        onClick={openEnquiry}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-40 group flex flex-col items-center gap-2 bg-[#17B352] hover:bg-[#22D060] text-black pl-2 pr-1.5 py-5 rounded-l-xl shadow-2xl shadow-[#17B352]/30 transition-colors"
      >
        <Mail className="w-4 h-4" />
        <span className="enquire-rail mono-font text-[10px] font-bold uppercase">Enquire · Contact Us</span>
      </button>

      <div className="pt-24 pb-24">
        {/* Compact header */}
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <p className="mono-font text-xs tracking-[0.3em] text-[#52525B] mb-2 uppercase">Our Work</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl italic leading-none" style={{ fontFamily: "'Instrument Serif', serif" }}>
              Portfolio
            </h1>
            <div className="mt-3 flex flex-wrap items-baseline gap-x-6 gap-y-1">
              <p className="text-[#A1A1AA] max-w-xl">
                Stories we've helped travel across languages and cultures.
              </p>
            </div>
          </motion.div>
        </div>

        {loading ? (
          <div className="text-center py-16 mono-font text-sm text-[#52525B]">Loading...</div>
        ) : shows.length === 0 ? (
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center py-16 glass-card rounded-xl">
              <h3 className="text-2xl mb-2 italic">Portfolio coming soon</h3>
              <p className="text-[#A1A1AA]">We're curating our latest work for showcase.</p>
            </div>
          </div>
        ) : (
          <>
            {/* Client Logos strip (toggleable) */}
            {display.show_client_logos && <ClientLogosStrip />}

            {/* Latest Projects */}
            {latestWithPoster.length > 0 && (
              <MarqueeRow
                title="Latest Projects"
                subtitle="Fresh from the studio · Powered by Iyuno"
                items={latestWithPoster}
                onClickShow={setSelected}
                durationSec={60}
              />
            )}

            {/* Full Catalog divider */}
            <div className="max-w-7xl mx-auto px-6 mt-4 mb-4">
              <div className="flex items-center gap-3">
                <span className="mono-font text-[10px] tracking-[0.3em] text-[#52525B] uppercase">Full Catalog</span>
                <div className="flex-1 h-px bg-[#1f1f23]" />
                <span className="mono-font text-[10px] tracking-[0.3em] text-[#52525B] uppercase">By Task</span>
              </div>
            </div>

            {/* By Service */}
            {serviceRows.map((row, i) => (
              <MarqueeRow
                key={`svc-${row.key}`}
                title={row.key}
                subtitle={describeTask(row.key)}
                items={row.items}
                onClickShow={setSelected}
                durationSec={70 + i * 8}
                reverse={i % 2 === 1}
              />
            ))}

            {/* By Studio (with Via Iyuno suffix) */}
            {clientRows.length > 0 && (
              <div className="max-w-7xl mx-auto px-6 mt-2 mb-4">
                <div className="flex items-center gap-3">
                  <span className="mono-font text-[10px] tracking-[0.3em] text-[#52525B] uppercase">By Studio</span>
                  <div className="flex-1 h-px bg-[#1f1f23]" />
                </div>
              </div>
            )}
            {clientRows.map((row, i) => (
              <MarqueeRow
                key={`cli-${row.key}`}
                title={`${row.key} (Via Iyuno)`}
                items={row.items}
                onClickShow={setSelected}
                durationSec={75 + i * 6}
                reverse={i % 2 === 0}
              />
            ))}

            {/* Also-in-catalog: titles without posters (toggleable) */}
            {display.show_no_poster && allNoPoster.length > 0 && (
              <>
                <div className="max-w-7xl mx-auto px-6 mt-6 mb-4">
                  <div className="flex items-center gap-3">
                    <span className="mono-font text-[10px] tracking-[0.3em] text-[#52525B] uppercase">More from our catalog</span>
                    <div className="flex-1 h-px bg-[#1f1f23]" />
                  </div>
                </div>
                <ChipRow
                  title="Also in our catalog"
                  subtitle="Additional projects from our archives, without poster artwork."
                  items={allNoPoster}
                  onClickShow={setSelected}
                />
              </>
            )}

            {/* Footer CTA */}
            <div className="max-w-3xl mx-auto px-6 mt-16 text-center">
              <p className="mono-font text-[10px] tracking-[0.3em] text-[#52525B] uppercase mb-3">+ many more</p>
              <h3 className="text-3xl md:text-4xl italic mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>
                Got a project to localize?
              </h3>
              <button
                type="button"
                onClick={openEnquiry}
                data-testid="footer-enquire-btn"
                className="inline-flex items-center gap-2 bg-[#17B352] hover:bg-[#22D060] text-black mono-font text-xs uppercase tracking-[0.2em] font-bold px-6 py-3 rounded-full transition-colors"
              >
                <Mail className="w-4 h-4" /> Start a Conversation
              </button>
            </div>
          </>
        )}
      </div>

      {selected && <ShowModal show={selected} onClose={() => setSelected(null)} />}
      <EnquiryModal open={enquiryOpen} onClose={() => setEnquiryOpen(false)} />
    </div>
  );
};

export default PortfolioPage;
